import { FC, ReactNode, useEffect, useState } from 'react';

import { ChevronUpIcon, PlusIcon } from '@/assets/icons';
import { Button, IconButton, Tag } from '@/components/atoms';
import { COMPARISON_DROPDOWN_TEST_IDS } from '@/constants/data-testids';
import { cn } from '@/utils/tailwind';

import {
  Dropdown,
  DropdownMenuClearSelection,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../Dropdown/Dropdown';
import { FilterDropdown, FilterDropdownOption } from '../FilterDropdown/FilterDropdown';

export type ComparisonOption = {
  value: string;
  label: string;
  leftIcon?: ReactNode;
};

type ComparisonDropdownProps = {
  comparisonGroups: ComparisonOption[];
  comparisonSegments: ComparisonOption[];
  selectedGroups: ComparisonOption[];
  selectedSegment: ComparisonOption | null;
  triggerLabel: string;
  triggerIcon?: ReactNode;
  triggerVariant?: 'default' | 'transparent';
  visibleGroupCount: number;
  setVisibleGroupCount: (value: number) => void;
  onChange: (selectedSegment: ComparisonOption | null, selectedGroups: ComparisonOption[]) => void;
  onClickClearSeletion?: () => void;
  minVisibleGroups?: number;
};

const DEFAULT_GROUP: ComparisonOption = { label: '', value: '' };

export const ComparisonDropdown: FC<ComparisonDropdownProps> = ({
  comparisonGroups,
  comparisonSegments,
  selectedGroups,
  selectedSegment,
  triggerLabel,
  triggerIcon,
  triggerVariant,
  visibleGroupCount,
  setVisibleGroupCount,
  onChange,
  onClickClearSeletion,
  minVisibleGroups = 2,
}) => {
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [localGroups, setLocalGroups] = useState<ComparisonOption[]>([]);

  const activeGroups = localGroups.filter(({ value, label }) => Boolean(value && label));
  const hasSelection =
    selectedSegment || activeGroups.length > 0 || localGroups.length > minVisibleGroups;
  const maxGroups = comparisonGroups.length;
  const canAddNewGroups = localGroups.length < maxGroups;
  const slicedGroups = localGroups.slice(0, visibleGroupCount);

  const handleChangeSegmentOptions = (newSegment: FilterDropdownOption | null) => {
    onChange(newSegment, localGroups);
  };

  const handleChangeComparsionGroups = (newGroup: FilterDropdownOption | null, index: number) => {
    if (!newGroup) return;
    const updated = [...localGroups];
    updated[index] = newGroup;
    setLocalGroups(updated);
    onChange(selectedSegment, updated);
  };

  const handleAddEmptyGroup = () => {
    if (!canAddNewGroups) return;

    const newGroups = [...localGroups, DEFAULT_GROUP];
    setLocalGroups(newGroups);
    setVisibleGroupCount(visibleGroupCount + 1);
    onChange(selectedSegment, newGroups);
  };

  useEffect(() => {
    const groups = [...selectedGroups];
    while (groups.length < minVisibleGroups) {
      groups.push(DEFAULT_GROUP);
    }
    setLocalGroups(groups);
  }, [selectedGroups, minVisibleGroups]);

  return (
    <Dropdown onOpenChange={setIsOpenDropdown}>
      <DropdownMenuTrigger variant={triggerVariant} asChild>
        <Button className="gap-2" variant="secondary">
          {triggerIcon}
          <span className="mr-1">{triggerLabel}</span>
          {activeGroups.length > 0 ? (
            <Tag size="regular" color="cyan" label={activeGroups.length.toString()} />
          ) : (
            <span className="text-custom-warm-grey-400">
              <ChevronUpIcon
                className={cn('transition-transform', isOpenDropdown ? 'rotate-180' : 'rotate-0')}
              />
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="min-w-[250px]">
        <div className="flex items-center gap-2.5 mb-1 px-3 py-2">
          <p className="text-body-14-regular text-custom-warm-grey-800">Compare</p>
          <FilterDropdown
            key={selectedSegment?.value}
            value={selectedSegment}
            options={comparisonSegments}
            onChange={handleChangeSegmentOptions}
            placeholder="Select segment"
          />
          <p className="text-body-14-regular text-custom-warm-grey-800">Segment</p>
        </div>

        <div className="px-3 py-2">
          <p className="text-body-14-regular text-custom-warm-grey-800 mb-2">Comparison groups</p>
          <div className="flex items-center gap-2.5 max-w-3xl flex-wrap">
            <div className="flex items-center gap-2.5">
              {slicedGroups.map((item, index) => {
                const selectedValues = localGroups.map(g => g.value);
                const filteredOptions = comparisonGroups.filter(
                  opt => opt.value === item.value || !selectedValues.includes(opt.value)
                );

                return (
                  <FilterDropdown
                    key={`${item.value}-${index}`}
                    value={item}
                    onChange={value => handleChangeComparsionGroups(value, index)}
                    options={filteredOptions}
                    placeholder="Select group"
                    hideChevronIcon={Boolean(item.value)}
                  />
                );
              })}
            </div>
            {canAddNewGroups && (
              <IconButton
                data-testid={COMPARISON_DROPDOWN_TEST_IDS.ADD_BUTTON}
                size="md"
                icon={<PlusIcon width={20} height={20} />}
                variant="secondary"
                onClick={handleAddEmptyGroup}
              />
            )}
          </div>
        </div>

        {hasSelection && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuClearSelection onClick={onClickClearSeletion}>
              Clear selection
            </DropdownMenuClearSelection>
          </>
        )}
      </DropdownMenuContent>
    </Dropdown>
  );
};
